import { useDownload, useOptimizationList, useOptimizationStatus, useSnackbar } from '@e2m-customer-portal/hooks';
import { ErrorComponent, Loading, LoadingError, PageTitle } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import { Fragment, MouseEvent, ReactElement, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Trans, useTranslation } from 'react-i18next';
import { FileDownloadIcon } from '../../assets/icons';
import { makeApiUrl } from '../../utils';

export function OptimizationDownload(): ReactElement {
  const [t] = useTranslation();
  const { showError } = useSnackbar();
  const { optimizedContractIds } = useOptimizationStatus()!;
  const { files, loading } = useOptimizationList(optimizedContractIds.map((id) => String(id)));
  const filenames = Array.from(new Set(files.map((file) => file.slice(0, file.lastIndexOf('.')))));
  const pdfFiles = files.filter((file: string) => file.toLowerCase().endsWith('.pdf'));
  const xlsxFiles = files.filter((file: string) => file.toLowerCase().endsWith('.xlsx'));

  const { clickHandler } = useDownload();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = (filename: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    setIsLoading(true);
    clickHandler({ filename })(event)
      .catch(() => showError(t('SHARED_ERROR_UNKNOWN')))
      .finally(() => setIsLoading(false));
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorComponent}>
      <LoadingError loading={false} error={undefined}>
        <PageTitle title={t('OPTIMIZATION_DOWNLOAD_HEADER')} />
        {loading ? (
          <Loading />
        ) : (
          <div className="bg-grey-50 h-full py-6 sm:px-6">
            {!filenames.length && !loading ? (
              <div className="mx-8 h-full flex-col text-sm leading-6 sm:mx-0 sm:w-full">
                <Trans
                  components={{
                    h1: <h1 className="mt-0 text-lg">{}</h1>,
                    a: (
                      <a
                        className="font-semibold text-green-200 no-underline hover:underline"
                        href="mailto:fahrplanoptimierung@e2m.energy"
                      >
                        {}
                      </a>
                    ),
                  }}
                >
                  {t('OPTIMIZATION_DOWNLOAD_NO_FILES_AVAILABLE')}
                </Trans>
              </div>
            ) : (
              <div className="w-full max-w-5xl">
                <div className="border-grey-200 grid min-w-max grid-cols-[1fr_max-content_max-content] border-0 border-b border-solid bg-white sm:border-x sm:border-t">
                  <div className="bg-blue-grey-200 hidden px-4 py-2 font-semibold text-white sm:block">
                    {t('SHARED_FORECAST')}
                  </div>
                  <div className="bg-blue-grey-200 col-span-2 hidden p-2 font-semibold text-white sm:block">
                    {t('OPTIMIZATION_DOWNLOAD_RESULT')}
                  </div>
                  {filenames.map((filename: string) => {
                    const pdfFile = pdfFiles.find((file: string) => file.includes(filename));
                    const xlsxFile = xlsxFiles.find((file: string) => file.includes(filename));
                    return (
                      <Fragment key={filename}>
                        <div className="border-grey-200 col-span-full flex w-[calc(100vw-2rem)] items-center justify-between truncate border-0 border-t border-solid p-4 sm:col-auto sm:w-[calc(100%-2rem)]">
                          <div className="text-grey-300 mr-4 sm:hidden">{t('SHARED_FORECAST')}</div>
                          <div className="truncate [direction:rtl] sm:min-w-full sm:max-w-0 sm:text-end">
                            {filename}
                          </div>
                        </div>
                        <div className="border-grey-100 bg-grey-50/50 text-grey-300 sm:border-grey-200 flex items-center border-0 border-t border-solid p-4 sm:hidden">
                          {t('OPTIMIZATION_DOWNLOAD_RESULT')}
                        </div>
                        <div className="border-grey-100 bg-grey-50/50 sm:border-grey-200 flex w-full items-center justify-center border-0 border-t border-solid sm:bg-transparent">
                          {pdfFile && (
                            <Button
                              className="my-2 ml-2 mr-1 flex gap-1"
                              href={makeApiUrl('optimization-download/file', {
                                fileName: pdfFile,
                              })}
                              onClick={handleClick(pdfFile)}
                              disabled={isLoading}
                            >
                              <FileDownloadIcon className="fill-white" />
                              {t('OPTIMIZATION_DOWNLOAD_PDF')}
                            </Button>
                          )}
                        </div>
                        <div className="border-grey-100 bg-grey-50/50 sm:border-grey-200 flex w-full items-center justify-center border-0 border-t border-solid sm:bg-transparent">
                          {xlsxFile && (
                            <Button
                              className="my-2 ml-1 mr-2 flex gap-1"
                              href={makeApiUrl('optimization-download/file', {
                                fileName: xlsxFile,
                              })}
                              onClick={handleClick(xlsxFile)}
                              disabled={isLoading}
                            >
                              <FileDownloadIcon className="fill-white" />
                              {t('OPTIMIZATION_DOWNLOAD_XLSX')}
                            </Button>
                          )}
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </LoadingError>
    </ErrorBoundary>
  );
}
